import { ErrorType } from "lib/types/address";
import { ShippingDeliveryIconType } from "components/common/ShippingHandleIcon";
import { UnApplicablePaymentReasonType } from "./payments";
import { CartNoteIcon } from "./cartNotes";
import { CartDialogType } from "./cart";
import { BannerConfig } from "./merchantConfigTypes";
import { GiftCardProviderType } from "./giftCards";

export type ExitSurveyOptionType = {
  optionId: string;
  optionLabel: string;
};

// Visual/presentation config authored in the dashboard and served via CHECKOUT ui-attributes.
export type ExitCouponNudgeVisualConfigType = {
  badge: {
    bgColor: string;
    textColor: string;
    htmlText: string;
  };
  nudge: {
    bgColor: string;
    textColor: string;
    htmlText: string;
  };
  ctaColor: string;
  couponValue: string;
  additionalSurvey: boolean;
  couponCode?: string;
};

// Per-checkout coupon eligibility/pricing resolved at runtime from the /exit-coupon API.
// Lives in CheckoutProvider; its presence is the "coupon is applicable, show the nudge" signal.
export type ExitCouponNudgeRuntimeType = {
  couponCode?: string;
  couponValue?: string;
  concessionAmount: number;
  amountType: "FLAT" | "PERCENTAGE";
  maximumConcessionAmount?: number;
  alreadyApplied?: boolean;
};

// Merged view the exit-coupon popups render: visual config overlaid with runtime pricing.
export type ExitCouponNudgeConfigType = ExitCouponNudgeVisualConfigType & ExitCouponNudgeRuntimeType;

export type CheckoutPayloadType = {
  merchant_id: string;
  items: CheckoutItemType[];
};

export type CheckoutItemType = {
  item_id: string;
  quantity: number;
  price: string;
};

export type ItemType = {
  item_id: string;
  item_title: string;
  quantity: number;
  price: string;
  image: string;
  is_freebie: boolean;
  variant_title: string;
  is_available: boolean;
  item_properties?: any;
  originalPrice: string;
  markupPrice: string;
  availableQuantity: number;
  isMutable: boolean;
  variant_id: string;
  product_id: string;
  item_url: string;
  is_upsell: boolean;
  is_discount_alteration: boolean;
  is_platform_fee: boolean;
  hasProductSelector: boolean;
  appliedItemAddOns: AddOnType[];
  rawItem: any;
  components: BundleItemType[];
  has_components: boolean;
  /** Index of this line in the cart/checkout items array from the API */
  itemIndex?: number;
};

export type BundleItemType = Pick<ItemType, "item_id" | "variant_id" | "image"> & {
  image: string;
  item_id: string;
  price: string;
  product_id: string;
  product_name: string;
  quantity: number;
  sku: string;
  variant_id: string;
  variant_name: string;
  variant_title: string;
  isUnservicable: boolean;
};

export type itemModificationRuleType = {
  showDelete: boolean;
  showIncrement: boolean;
  showDecrement: boolean;
  redirectOnClick: boolean;
  showQuantityEditor: boolean;
};

export type AddOnType = {
  addon_id: string;
  item_id: string | null;
  id: string;
  addon_level: AddOnLevelType;
  price: number;
  removed: boolean | null;
  addon_type: "CART_NOTE" | "DEFAULT";
  note: string;
  variant_id?: string;
};

export type AddOnLevelType = "ITEM" | "CART";
export type UIAssetType = "CART" | "CHECKOUT" | "DEFAULT";

export type AddOnMetaType = {
  addon_id: string;
  addon_type: "CART_NOTE" | "DEFAULT";
  addon_level: AddOnLevelType;
  description: string;
  price: number;
  variant_id: string;
  show_image: boolean;
  add_line_item: boolean;
  default_selected: boolean;
  product_name: string;
  image: string;
  ui_asset: UIAssetType;
  description_template: string;
  /** Cart item indexes this addon should appear on. Empty for cart-level addons. */
  addon_reference_id?: number[];
  metadata: {
    icon: CartNoteIcon;
    title: string;
    suggestion_text: string;
  };
};

export type BillingType = {
  total_payable: number;
  sub_total: number;
  total: number;
  rewards: number;
  tax: number;
  discount: number;
  shipping: number;
  cod: number;
  cod_enabled: boolean;
  taxes_included: boolean;
  serviceable: boolean;
  prepaid_discount: number;
  prepaid_total: number;
  original_sub_total: number;
  addon_total: number;
  gift_card: number;
  convenience_fee?: number;
};

export type CheckoutMainPages = "AUTH" | "ADDRESS_LIST" | "PAYMENTS";

export type CheckoutViewType = CheckoutMainPages | "NA" | "SSO" | "ORDER_SUMMARY";

export type CheckoutModalType =
  | "CHOOSE_PLAN"
  | "ADDRESS_NEW"
  | "ADDRESS_EDIT"
  | "SHIPPING_HANDLES"
  | "DISCOUNT_LIST"
  | "PAYMENT_METHOD"
  | "PAYMENT_GROUP"
  | "MAIL_COLLECT"
  | "REPLACE_COUPON"
  | "ADDRESS_NEW"
  | "ADDRESS_EDIT"
  | "ADDRESS_LIST"
  | "BILLING_ADDRESS_NEW"
  | "PROCESSING"
  | "AUTH"
  | "NONE"
  | "COMMON"
  | "EXIT_CONFIRMATION"
  | "COD_CONFIRMATION"
  | "DELETE_ADDRESS_CONFIRMATION"
  | "GIFT_CARDS"
  | "OOS"
  | "REMOVE_ITEM"
  | "OFFLINE_PAYMENT_CONFIRMATION"
  | "COD_ORDER_PLACING"
  | "COD_OTP"
  | "REMOVE_OFFERS"
  | "EXIT_SURVEY"
  | "UPSELL_VARIANT_SELECTOR"
  | "UPI_INTENT"
  | "REWARD_WALLET_CONFIRMATION"
  | "LOYALTY_CONFIRMATION"
  | "LOYALTY_INFORMATION"
  | "LOADER"
  | "UNSERVICEABLE_PRODUCTS"
  | "GYFTR_ADD_COUPON_DIALOG"
  | "GYFTR_REDEEM_VOUCHER_DIALOG"
  | "GYFTR_SWITCH_COUPON_DIALOG"
  | "COMBINED_PRODUCT_REMOVE"
  | "ADD_SHOPIFY_GC"
  | "GIFT_CARD_SWITCH_COUPON_DIALOG"
  | "DELHIVERY_CONFIRMATION"
  | "ITEM_PROPERTIES";

export type DiscountProductSelectorModalType = "NONE" | "DISCOUNTED_PRODUCT_SELECTOR" | "FREE_GIFTS";

export type ShippingHandleType = {
  handle_name: string;
  id: string;
  price: string;
  selected_handle: boolean;
  cod_enabled: boolean;
  cod_extra_price: string;
  etd?: string;
  etd_text?: string;
  online_enabled: boolean;
  icon?: ShippingDeliveryIconType;
  min_days_to_deliver?: number;
  max_days_to_deliver?: number;
  original_price?: string;
};

export type ShippingHandlesType = ShippingHandleType[];

export type LoyaltyRedemptionHandle = {
  id: string;
  title: string;
  desc: string;
  totalPoints: number;
  totalPrice: number;
  provider:
    | "YOTPO"
    | "POP_COINS"
    | "NECTOR"
    | "DAEIOU_JEWELS"
    | "YOUR_TOKEN"
    | "RETENZY"
    | "CASA"
    | "DOT_AND_KEY"
    | "FLITS"
    | "REWARD_RALLY"
    | "E_REWARDS"
    | "KIEHLS"
    | "PURITY_COINS"
    | "SHOPIFY_STORE_CREDITS"
    | "STORE_TO_APP"
    | "SUPERCOINS"
    | "RETAINLEY"
    | "OPT_CULTURE"
    | "NONE";
};

export type NetworkLoyaltyType =
  | {
      provider: "YOTPO";
      value: YotpoLoyaltyType;
      isVerified: boolean;
      accountExists: boolean;
    }
  | {
      provider: "POP_COINS";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "NECTOR";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "DAEIOU_JEWELS";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "YOUR_TOKEN";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "CASA";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "DOT_AND_KEY";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "RETENZY";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "FLITS";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "REWARD_RALLY";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "E_REWARDS";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "KIEHLS";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "PURITY_COINS";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "SHOPIFY_STORE_CREDITS";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "STORE_TO_APP";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "SUPERCOINS";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "RETAINLEY";
      value: StandardNetworkLoyaltyType;
    }
  | {
      provider: "OPT_CULTURE";
      value: StandardNetworkLoyaltyType;
    };

export type YotpoLoyaltyType = {
  email: string;
  totalPointsBalance: number;
  totalReducedPoints: number;
  totalReductionAmount: number;
  redemptionOptions: LoyaltyRedemptionHandle[];
  coinName: string;
  coinNamePlural: string;
  unavailabilityReasons: UnApplicablePaymentReasonType[];
  isAvailable: boolean;
};

export type StandardNetworkLoyaltyType = {
  provider:
    | "POP_COINS"
    | "NECTOR"
    | "DAEIOU_JEWELS"
    | "YOUR_TOKEN"
    | "RETENZY"
    | "CASA"
    | "DOT_AND_KEY"
    | "FLITS"
    | "REWARD_RALLY"
    | "E_REWARDS"
    | "KIEHLS"
    | "PURITY_COINS"
    | "SHOPIFY_STORE_CREDITS"
    | "STORE_TO_APP"
    | "SUPERCOINS"
    | "RETAINLEY"
    | "OPT_CULTURE"
    | "NONE";
  earnValue: {
    isEnabled: boolean;
    issuanceRate: number;
    normalizedValue: number;
    value: number;
  };
  redemptionId: string;
  coinName: string;
  coinNamePlural: string;
  userDetails: {
    customerId: string;
    pointBalance: number;
    maxApplicablePoints: number;
    reductionAmount: number;
  };
  unavailabilityReasons: UnApplicablePaymentReasonType[];
  isAvailable: boolean;
  currencyBurnedMessage: string;
  burnTileCustomization?: {
    coin_icon_url?: string;
  };
};

export type FloWalletData = {
  totalPointsBalance: number;
  totalAmount: number;
  maxBurnablePoints: number;
  maxBurnableAmount: number;
  maxEarnablePoints: number;
  maxEarnableAmount: number;
  earnMultiplier: number;
  burnMultiplier: number;
  coinName: string;
  coinNamePlural: string;
  earnEnabled: boolean;
  burnEnabled: boolean;
  walletDisabledMessage: string;
  insufficientBalanceMessage: string;
  coinConversionRate: number;
  personalizedWalletMessageEnabled?: boolean;
  rewardsEarnedMessage?: string;
  currencyEarnedMessage?: string;
  currencyBurnedMessage?: string;
  currencyBurnedTemplate?: string;
  earnTileCustomization?: {
    text_color: string;
    outline_color: string;
    background_color: string;
    is_credit_name_visible: string;
    outline: string;
  };
  unavailabilityReasons: UnApplicablePaymentReasonType[];
  isAvailable: boolean;
};

export type FloWalletRedemptionHandle = {
  earnMultiplier: number;
  totalReducedPoints: number;
  totalReductionAmount: number;
};

export type ThirdPartyAuthType = {
  provider: "TRUECALLER";
  redirectUrl: string;
  requestId: string;
};

export type BannerConfigType = {
  type: "AUTH" | "ORDER_SUMMARY" | "PAYMENTS" | "CART";
  backgroundColor: string;
  enabled: boolean;
  htmlTextString: string;
  textColor: string;
  multipleBannersEnabled: boolean;
  animationStyle?: string;
  position?: number;
};

export type UpSellConfigType = {
  isEnabled: boolean;
  title: string;
  discountedTitle: string;
  shouldOverrideDiscountedUpsell: boolean;
};

export type wishlistVisibilityConfigType = {
  enabled: boolean;
  header: string;
  emptyHeader: string;
};

export type WishlistConfigType = {
  isEnabled: boolean;
  config: {
    checkout: wishlistVisibilityConfigType;
    cart: wishlistVisibilityConfigType;
    store: wishlistVisibilityConfigType;
  };
};

export enum CheckoutActions {
  ADDRESS_SELECT = "ADDRESS_SELECT",
}

export type ActionUrlsType = {
  [key in CheckoutActions]: {
    failure_url: string;
    success_url: string;
  };
};

export type CommonSectionType =
  | "HEADER_SECTION"
  | "TOP_SECTION"
  | "ORDER_SUMMARY_SECTION"
  | "COUPONS_SECTION"
  | "REWARDS_SECTION"
  | "UPSELL_SECTION"
  | "AUTH_PHONE_SECTION"
  | "NONE";

export type CheckoutSectionType =
  | CommonSectionType
  | "SHIPPING_ADDRESS_SECTION"
  | "BILLING_ADDRESS_SECTION"
  | "SHIPPING_HANDLES_SECTION"
  | "ADD_ON_SECTION"
  | "SUBSCRIPTION_PLAN_SECTION"
  | "PAYMENTS_SECTION";

export type CheckoutValidationType = {
  isValid: boolean;
};

export type CheckoutAddressValidationType = CheckoutValidationType & {
  metadata?: {
    addressId: string;
    fieldData?: Record<string, ErrorType | undefined>;
  };
};

export type CartLoginValidationType = CheckoutValidationType & {
  metadata?: {
    message: string;
  };
};

export type CheckoutValidationsType = {
  address: CheckoutAddressValidationType;
  shipping: CheckoutValidationType;
  cartLoginPopup: CartLoginValidationType;
};

export type CouponReplacementInvalidityReasonType =
  | "DISCOUNTS_NOT_COMBINABLE"
  | "NO_MORE_STACK_LENGTH"
  | "NONE"
  | "REWARDS_NOT_COMBINABLE";

export type CouponReplacementType = {
  showPopup: boolean;
  appliedDiscountCode: string;
  invalidDiscountCodes: string[];
  invalidityReason: CouponReplacementInvalidityReasonType;
  appliedCouponTitle?: string;
};

export type SavingItemsType = {
  savings: number;
  count: number;
  manualFreebieCount: number;
};

export enum CouponType {
  PREPAID = "PREPAID",
  REWARD_AS_DISCOUNT = "REWARD_AS_DISCOUNT",
  PAYMENT_OFFER = "PAYMENT_OFFER",
  BXGY = "BXGY",
  SHIPPING = "SHIPPING",
  CALLOUT_CARD = "CALLOUT_CARD",
  BUNDLE = "BUNDLE",
  AMOUNT = "AMOUNT",
  PG_OFFERS = "PG_OFFERS",
  EXIT_COUPON = "EXIT_COUPON",
  PARTNER_OFFER = "PARTNER_OFFER",
}

export type CheckoutExpiredType = {
  expired: boolean;
  isInvalidUser: boolean;
};

export type SelectedShippingHandleType = {
  etd: string;
  icon: ShippingDeliveryIconType;
  etd_text: string;
  min_days_to_deliver?: number;
  max_days_to_deliver?: number;
};

export type DiscountedProductSelectorDataType = {
  totalItems: number;
  totalSelectedItems: number;
  items: DiscountedProductSelectorItemType[];
};

export type DiscountedProductSelectorItemType = {
  couponCode: string;
  quantity: number;
  selectedQuantity: number;
  concessionAmount: number;
  products: DiscountedProductSelectorProductType[];
};

export type DiscountedProductSelectorProductType = {
  id: string;
  name: string;
  primaryImageUrl: string;
  variants: DiscountedProductSelectorProductVariantType[];
};

export type DiscountedProductSelectorProductVariantType = {
  id: string;
  name: string;
  selectedQuantity: number;
  isOutOfStock: boolean;
  inventoryQuantity: number;
  inventoryPolicy: "CONTINUE" | "DENY";
  originalPrice: number;
  currentPrice: number;
  discountedPrice: number;
  imageUrl: string;
  hasComponents: boolean;
};

export type DiscountedProductSelectorProductDistributionType = {
  freebies: number;
  discountedProducts: number;
  onlyFreebies: boolean;
  onlyDiscountedProducts: boolean;
  freebiesAndDiscountedProducts: boolean;
};

export type DiscountedProductSelectorPayloadType = {
  selection: {
    discount_code: string;
    items: {
      variant_id: string;
      quantity: number;
      has_components: boolean;
    }[];
  }[];
};

export enum BannerType {
  HEADER = "HEADER",
  FOOTER = "FOOTER",
}

export enum CheckoutErrorResponseCode {
  // Internal Server Error
  UARC_001 = "UARC_001",
  // No Result Found
  UARC_002 = "UARC_002",
  // Missing Request Parameters
  UARC_003 = "UARC_003",
  // Rule Group Not Active
  UARC_004 = "UARC_004",
  // Selected Address Not Serviceable
  UARC_005 = "UARC_005",
  // Add Items Worth Delivery
  UARC_006 = "UARC_006",
  // Minimum Weight of Order
  UARC_007 = "UARC_007",
  // Single Product is Not Serviceable
  UARC_008 = "UARC_008",
  // Multiple Products are Not Serviceable
  UARC_009 = "UARC_009",
  // Selected Address is Not Serviceable
  UARC_010 = "UARC_010",
}

export type CheckoutErrorResponse = {
  code: CheckoutErrorResponseCode;
  message: string;
  metadata?: {
    items?: {
      item_id: string;
    }[];
  };
};

export type NoteAttributesType = {
  name: string;
  value: string;
};

export type CombinedProductRemovalItemsType = {
  selectedItem: {
    id: string;
    variantId: string;
  };
  removableVariantIds: Array<{
    quantity: number;
    variant_id: string;
  }>;
};

export interface AuthenticatedArgs {
  setDialog: (dialog: CartDialogType) => void;
  billing?: BillingType;
  isLoading?: boolean;
  primaryCTAText: string;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  bottomBannerConfig?: BannerConfig;
}

export interface ItemCardProps extends ItemType {
  disableOOSItems: boolean;
  checkoutItemsMutable: boolean;
  isCartItem?: boolean;
  isUpdating?: boolean;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  itemLevelAddons?: AddOnMetaType[];
  isUnservicable?: boolean;
}

export type GiftCards = {
  gift_card_type: string;
  amount: number;
  transaction_id?: string;
  card_number?: string;
  masked_number?: string;
  provider?: GiftCardProviderType;
};

export type ItemLevelETDItemType = {
  variantId: string;
  days?: {
    min?: number;
    max?: number;
  };
  date?: {
    min?: string;
    max?: string;
  };
  edt_message?: string;
};

export type ItemLevelETDType = ItemLevelETDItemType[];
